// Admin endpoints for corporate management (core-lambda).
// All require an admin-bearing token (is_admin=true claim).

import axios from "axios";
import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

const ADMIN_BASE = "/api/admin/enterprises";

// ─── Corporates ────────────────────────────────────────────────────────────

export const listCorporates = async ({
  page = 1,
  page_size = 20,
  status,
  search,
} = {}) => {
  const response = await axiosAssessmentInstance.get(ADMIN_BASE, {
    params: { page, page_size, status, search },
  });
  const payload = unwrap(response, {});
  const items = payload?.items || payload?.corporates || [];
  const total = payload?.total ?? items.length;
  const ps = payload?.page_size || page_size;
  const total_pages = payload?.total_pages ?? Math.max(1, Math.ceil(total / ps));
  return { corporates: items, total, page: payload?.page ?? page, total_pages };
};

export const getCorporate = async (id) => {
  const response = await axiosAssessmentInstance.get(`${ADMIN_BASE}/${id}`);
  return unwrap(response);
};

export const createCorporate = async (payload) => {
  const response = await axiosAssessmentInstance.post(ADMIN_BASE, payload);
  return unwrap(response);
};

export const updateCorporate = async (id, patch) => {
  const response = await axiosAssessmentInstance.patch(`${ADMIN_BASE}/${id}`, patch);
  return unwrap(response);
};

export const activateCorporate = async (id) => {
  const response = await axiosAssessmentInstance.post(`${ADMIN_BASE}/${id}/activate`);
  return unwrap(response);
};

export const deactivateCorporate = async (id) => {
  const response = await axiosAssessmentInstance.post(`${ADMIN_BASE}/${id}/deactivate`);
  return unwrap(response);
};

// ─── Logo upload ───────────────────────────────────────────────────────────

export const getLogoUploadUrl = async (id, fileExtension) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/logo/upload-url`,
    { file_extension: fileExtension }
  );
  return unwrap(response);
};

export const getHeroImageUploadUrl = async (id, fileExtension) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/content/activation/hero_image/upload-url`,
    { file_extension: fileExtension }
  );
  return unwrap(response);
};

const extToContentType = (ext) => {
  const e = String(ext || "").toLowerCase();
  if (e === "jpg" || e === "jpeg") return "image/jpeg";
  if (e === "png") return "image/png";
  return "application/octet-stream";
};

/**
 * Upload a logo file to a presigned S3 PUT URL. Uses a bare axios call (no
 * interceptors) since the URL already carries S3 auth and we must NOT send
 * our Bearer token to S3.
 */
export const uploadLogoToS3 = async (uploadUrl, file, fileExtension) =>
  axios.put(uploadUrl, file, {
    headers: { "Content-Type": extToContentType(fileExtension) },
  });

// ─── Content (CMS) ─────────────────────────────────────────────────────────

export const getContentPages = async (id) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/content/pages`
  );
  return unwrap(response, { pages: {} });
};

export const getPageContent = async (id, pageKey) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/content/${pageKey}`
  );
  return unwrap(response, { sections: [] });
};

export const getSectionContent = async (id, pageKey, sectionKey) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/content/${pageKey}/${sectionKey}`
  );
  return unwrap(response);
};

export const upsertSectionDraft = async (id, pageKey, sectionKey, content) => {
  const response = await axiosAssessmentInstance.put(
    `${ADMIN_BASE}/${id}/content/${pageKey}/${sectionKey}`,
    { content }
  );
  return unwrap(response);
};

export const publishSection = async (id, pageKey, sectionKey) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/content/${pageKey}/${sectionKey}/publish`
  );
  return unwrap(response);
};

export const getPageConfig = async (id, pageKey) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/content/${pageKey}/config`
  );
  return unwrap(response);
};

export const updatePageConfig = async (
  id,
  pageKey,
  { section_order, hidden_section_keys }
) => {
  const response = await axiosAssessmentInstance.put(
    `${ADMIN_BASE}/${id}/content/${pageKey}/config`,
    { section_order, hidden_section_keys }
  );
  return unwrap(response);
};

export const publishPage = async (id, pageKey) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/content/${pageKey}/publish`
  );
  return unwrap(response);
};

// ─── Lookup values (departments / roles) ───────────────────────────────────

export const listLookupValues = async (id, kind, { active_only } = {}) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/lookup-values/${kind}`,
    { params: { active_only } }
  );
  return unwrap(response, { items: [] });
};

export const createLookupValue = async (id, kind, body) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/lookup-values/${kind}`,
    body
  );
  return unwrap(response);
};

export const bulkCreateLookupValues = async (id, kind, names) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/lookup-values/${kind}/bulk`,
    { names }
  );
  return unwrap(response);
};

export const updateLookupValue = async (id, kind, valueId, patch) => {
  const response = await axiosAssessmentInstance.patch(
    `${ADMIN_BASE}/${id}/lookup-values/${kind}/${valueId}`,
    patch
  );
  return unwrap(response);
};

export const deleteLookupValue = async (id, kind, valueId) => {
  const response = await axiosAssessmentInstance.delete(
    `${ADMIN_BASE}/${id}/lookup-values/${kind}/${valueId}`
  );
  return unwrap(response);
};

// ─── Members ───────────────────────────────────────────────────────────────

export const listMembers = async (
  id,
  { page = 1, page_size = 20, status, search } = {}
) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/members`,
    { params: { page, page_size, status, search } }
  );
  const payload = unwrap(response, {});
  const items = payload?.items || payload?.members || [];
  const total = payload?.total ?? items.length;
  const ps = payload?.page_size || page_size;
  const total_pages = payload?.total_pages ?? Math.max(1, Math.ceil(total / ps));
  return { members: items, total, page: payload?.page ?? page, total_pages };
};

export const getMember = async (id, memberId) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/members/${memberId}`
  );
  return unwrap(response);
};

export const createMember = async (id, body) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/members`,
    body
  );
  return unwrap(response);
};

/**
 * @param {string} id corporate id
 * @param {File} file
 * @returns {Promise<import('../types/corporate').CsvImportResult>}
 */
export const importMembersCsv = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/members/import-csv`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(response);
};

export const reactivateMember = async (id, memberId) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/members/${memberId}/activate`
  );
  return unwrap(response);
};

export const deactivateMember = async (id, memberId, reason) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/members/${memberId}/deactivate`,
    { reason }
  );
  return unwrap(response);
};

export const departMember = async (id, memberId, reason) => {
  const response = await axiosAssessmentInstance.post(
    `${ADMIN_BASE}/${id}/members/${memberId}/depart`,
    { reason }
  );
  return unwrap(response);
};

export const deleteMember = async (id, memberId) => {
  const response = await axiosAssessmentInstance.delete(
    `${ADMIN_BASE}/${id}/members/${memberId}`
  );
  return unwrap(response);
};

// ─── Subscription settings ─────────────────────────────────────────────────

export const getSubscriptionSettings = async (id) => {
  const response = await axiosAssessmentInstance.get(
    `${ADMIN_BASE}/${id}/subscription-settings`
  );
  return unwrap(response);
};

export const putSubscriptionSettings = async (id, body) => {
  const response = await axiosAssessmentInstance.put(
    `${ADMIN_BASE}/${id}/subscription-settings`,
    body
  );
  return unwrap(response);
};

export const listSubscriptionPlans = async ({ billing_cycle } = {}) => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/subscription-plans",
    { params: { billing_cycle } }
  );
  return unwrap(response, []);
};
